import type { Move } from '../../../schemas/move';

/**
 * Mosse avanzate del Bardo L2-5 (manuale V1, rr. 2419-2502). 16 mosse.
 *
 * Catene di prerequisiti notabili:
 *  - 5 mosse richiedono *Parole di Potere* (Scolpita nella Roccia, Verbo
 *    Dirompente, Eco Interiore, Parole Lenitive, Lingua Affilata)
 *  - 2 mosse richiedono *Conoscenze Bardiche* (Vaste Conoscenze, Conoscitore
 *    di Leggende)
 *  - **Catena multiclasse a 3 step**: *Versatile* → *Eclettico* → *Poliedrico*
 *    (manuale rr. 2476-2491)
 *  - **Catena "iniziali aggiuntive" a 2 step**: *Di Tutto un Po'* → *Un Po'
 *    di Tutto*
 *
 * I vincoli "scegli da una classe **diversa**" di Eclettico/Poliedrico sono
 * regole di runtime sullo stato del PG (quale classe scelta in Versatile);
 * vivono in description, non in `prerequisites`.
 */

const scolpitaNellaRoccia: Move = {
  id: 'bardo.scolpita-nella-roccia',
  name: 'Scolpita nella Roccia',
  type: 'advanced_2_5',
  classId: 'bardo',
  trigger:
    'Quando materializzi il potere della Parola vergando dei caratteri o delle rune su di una superficie',
  description:
    "Scegli un effetto:\n\n- il bersaglio è vittima di un'illusione visiva e/o sonora a tua scelta, finché non si allontana;\n- il bersaglio sente un suono straziante che lo costringe a fuggire immediatamente;\n- tutti i sensi del bersaglio sono offuscati per qualche minuto o fino a quando non viene attaccato.\n\nDecidi se la magia interesserà solo la prima creatura senziente che vede ciò che hai scritto o anche chi le sta vicino in quel momento (esclusi te e i tuoi alleati). Quindi tira+**INT**. Con un 10+, tutto funziona come volevi. Con un 7-9, scegli tra potenza, durata e numero di bersagli. Il GM ti dirà se saranno maggiori o minori del previsto, ma in ogni caso ciò ti complicherà la vita.",
  roll: {
    stat: 'intelligenza',
    on10plus: { text: 'Tutto funziona come volevi.' },
    on7to9: {
      text: 'Scegli tra potenza, durata o numero di bersagli; il GM aggiusta gli altri parametri complicandoti la vita.',
    },
  },
  tags: [],
  prerequisites: ['bardo.parole-di-potere'],
  replaces: [],
};

const verboDirompente: Move = {
  id: 'bardo.verbo-dirompente',
  name: 'Verbo Dirompente',
  type: 'advanced_2_5',
  classId: 'bardo',
  trigger: "Quando fai vibrare all'estremo una singola Parola, riversandovi tutto il tuo potere",
  description:
    'Scegli un bersaglio e tira+**COS**. Con 7+, infliggi 1d8 danni con etichette *sonoro* e *ignora armatura* e il bersaglio è assordato per qualche minuto. Con 7-9, le vibrazioni riverberano anche su un altro bersaglio nelle vicinanze, a scelta del GM.',
  roll: {
    stat: 'costituzione',
    on10plus: { text: 'Infliggi 1d8 danni (sonoro, ignora armatura), bersaglio assordato.' },
    on7to9: {
      text: 'Stesso effetto ma le vibrazioni riverberano su un altro bersaglio (GM sceglie).',
    },
  },
  tags: [],
  prerequisites: ['bardo.parole-di-potere'],
  replaces: [],
};

const ecoInteriore: Move = {
  id: 'bardo.eco-interiore',
  name: 'Eco Interiore',
  type: 'advanced_2_5',
  classId: 'bardo',
  trigger: 'In ogni momento (estende *Parole di Potere*)',
  description: 'Puoi applicare gli effetti di *Parole di Potere* anche a te stesso.',
  tags: [],
  prerequisites: ['bardo.parole-di-potere'],
  replaces: [],
};

const paroleLenitive: Move = {
  id: 'bardo.parole-lenitive',
  name: 'Parole Lenitive',
  type: 'advanced_2_5',
  classId: 'bardo',
  trigger: 'Quando usi *Parole di Potere* per guarire',
  description: 'Curi +1d8 PF aggiuntivi.',
  tags: [],
  prerequisites: ['bardo.parole-di-potere'],
  replaces: [],
};

const linguaAffilata: Move = {
  id: 'bardo.lingua-affilata',
  name: 'Lingua Affilata',
  type: 'advanced_2_5',
  classId: 'bardo',
  trigger: 'Quando conferisci danni bonus con *Parole di Potere*',
  description: 'Il tuo bersaglio infligge +1d4 danni aggiuntivi.',
  tags: [],
  prerequisites: ['bardo.parole-di-potere'],
  replaces: [],
};

const vasteConoscenze: Move = {
  id: 'bardo.vaste-conoscenze',
  name: 'Vaste Conoscenze',
  type: 'advanced_2_5',
  classId: 'bardo',
  trigger: 'In ogni momento (estende *Conoscenze Bardiche*)',
  description: 'Aggiungi due nuove aree di specializzazione a *Conoscenze Bardiche*.',
  tags: [],
  prerequisites: ['bardo.conoscenze-bardiche'],
  replaces: [],
};

const conoscitoreDiLeggende: Move = {
  id: 'bardo.conoscitore-di-leggende',
  name: 'Conoscitore di Leggende',
  type: 'advanced_2_5',
  classId: 'bardo',
  trigger:
    'Quando *declami conoscenze* riguardo qualcosa che rientra in una delle tue aree di specializzazione di *Conoscenze Bardiche*',
  description: 'Ottieni +1 al tiro.',
  tags: [],
  prerequisites: ['bardo.conoscenze-bardiche'],
  replaces: [],
};

const sempreAlCentroDellaScena: Move = {
  id: 'bardo.sempre-al-centro-della-scena',
  name: 'Sempre al Centro della Scena',
  type: 'advanced_2_5',
  classId: 'bardo',
  trigger: 'Quando *aiuti o ostacoli* qualcuno con successo',
  description: 'Prendi +1 al prossimo tiro per usare una mossa.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const raggirare: Move = {
  id: 'bardo.raggirare',
  name: 'Raggirare',
  type: 'advanced_2_5',
  classId: 'bardo',
  trigger: 'Quando *parlamenti* con qualcuno',
  description: 'Con un 7+, prendi anche +1 al prossimo tiro contro di lui.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const celebrita: Move = {
  id: 'bardo.celebrita',
  name: 'Celebrità',
  type: 'advanced_2_5',
  classId: 'bardo',
  trigger: 'Quando hai a che fare con un PNG che ti ammira',
  description:
    "Puoi offrirgli il tuo autografo come leva per *parlamentare*. Inoltre, quando gli dai anche un prezioso ricordo dalle tue avventure (un'arma che hai usato, un tesoro degno di nota che hai trovato, il pegno di un amore o il cimelio di una vittoria), considera di aver ottenuto un risultato di 12+ per *parlamentare* con lui, senza bisogno di tirare i dadi: farà ciò che gli chiedi senza fare domande o chiedere altro in cambio.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const tenereIlRitmo: Move = {
  id: 'bardo.tenere-il-ritmo',
  name: 'Tenere il Ritmo',
  type: 'advanced_2_5',
  classId: 'bardo',
  trigger: 'Quando un avversario con cui hai già scambiato alcuni colpi ti attacca',
  description:
    "Tira+**DES**. Con 7-9, eviti gli effetti del suo attacco. Con 10+, inoltre, ottieni +1 al prossimo tiro di *taglia e spacca* o *raffica* contro di lui. Con un 6-, però, oltre a subire l'attacco avversario, ti metti anche in una posizione scomoda.",
  roll: {
    stat: 'destrezza',
    on10plus: { text: "Eviti l'attacco + +1 al prossimo taglia e spacca o raffica contro di lui." },
    on7to9: { text: "Eviti gli effetti dell'attacco." },
    on6minus: { text: "Subisci l'attacco e ti metti in posizione scomoda." },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const versatile: Move = {
  id: 'bardo.versatile',
  name: 'Versatile',
  type: 'advanced_2_5',
  classId: 'bardo',
  trigger: 'Al level-up (livello 2-5), come scelta multiclasse',
  description:
    "Mossa *multiclasse*. Scegli una mossa non multiclasse da un'altra classe a tua scelta.",
  tags: ['multiclasse'],
  prerequisites: [],
  replaces: [],
  // FI-17 (D7): pool aperto senza cap livello.
  multiclass: {
    picks: { kind: 'any-other-class' },
  },
};

const eclettico: Move = {
  id: 'bardo.eclettico',
  name: 'Eclettico',
  type: 'advanced_2_5',
  classId: 'bardo',
  trigger: 'Al level-up (livello 2-5), come scelta multiclasse',
  description:
    'Mossa *multiclasse*. Prendi una mossa non multiclasse da una classe **che non sia la stessa** da cui hai scelto la mossa quando hai preso *Versatile*.',
  tags: ['multiclasse'],
  prerequisites: ['bardo.versatile'],
  replaces: [],
  // FI-17 (D7): pool aperto + vincolo storico (classe diversa da *Versatile*).
  multiclass: {
    picks: { kind: 'any-other-class' },
    excludesClassesPickedInMoves: ['bardo.versatile'],
  },
};

const poliedrico: Move = {
  id: 'bardo.poliedrico',
  name: 'Poliedrico',
  type: 'advanced_2_5',
  classId: 'bardo',
  trigger: 'Al level-up (livello 2-5), come scelta multiclasse',
  description:
    'Mossa *multiclasse*. Prendi una mossa non multiclasse da una classe **che non sia la stessa** da cui hai scelto la mossa quando hai preso *Versatile* o *Eclettico*.',
  tags: ['multiclasse'],
  prerequisites: ['bardo.eclettico'],
  replaces: [],
  // FI-17 (D7): pool aperto + vincolo storico (classe diversa da *Versatile* / *Eclettico*).
  multiclass: {
    picks: { kind: 'any-other-class' },
    excludesClassesPickedInMoves: ['bardo.versatile', 'bardo.eclettico'],
  },
};

const diTuttoUnPo: Move = {
  id: 'bardo.di-tutto-un-po',
  name: "Di Tutto un Po'",
  type: 'advanced_2_5',
  classId: 'bardo',
  trigger: 'Al level-up (livello 2-5)',
  description: 'Scegli una mossa iniziale del Bardo che non hai già preso.',
  tags: [],
  prerequisites: [],
  replaces: [],
  // FI-31 (D5): concede una mossa iniziale residua dal gruppo del Bardo
  // (`pickCount: 2` su 4 → 2 residui dopo il L1).
  grantsMoveChoice: { kind: 'from-group', groupId: 'mosse-iniziali-aggiuntive' },
};

const unPoDiTutto: Move = {
  id: 'bardo.un-po-di-tutto',
  name: "Un Po' di Tutto",
  type: 'advanced_2_5',
  classId: 'bardo',
  trigger: 'Al level-up (livello 2-5)',
  description: "Scegli l'ultima mossa iniziale del Bardo che non hai già preso.",
  tags: [],
  prerequisites: ['bardo.di-tutto-un-po'],
  replaces: [],
  // FI-31 (D5): catena 2-step dopo *Di Tutto un Po'* — pool=1 residuo.
  grantsMoveChoice: { kind: 'from-group', groupId: 'mosse-iniziali-aggiuntive' },
};

export const bardoAdvanced2to5Moves: readonly Move[] = [
  scolpitaNellaRoccia,
  verboDirompente,
  ecoInteriore,
  paroleLenitive,
  linguaAffilata,
  vasteConoscenze,
  conoscitoreDiLeggende,
  sempreAlCentroDellaScena,
  raggirare,
  celebrita,
  tenereIlRitmo,
  versatile,
  eclettico,
  poliedrico,
  diTuttoUnPo,
  unPoDiTutto,
] as const;
